import React, { useState } from "react";
import DateSelection from "./DateSelection";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faTimes, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import AudioPlayer from "./AudioPlayer";
import { countryCodes } from "../../utils/countryCodes";

const BookingFlow = ({
  userData,
  providerId,
  selectedServiceDetail,
  selectedAddons,
  setSelectedAddons,
  availability,
  bookings,
  blockedTimes = [], // Provider's blocked time slots
  setShowBookingFlow,
  selectedTimezone,
  onTimezoneChange,
  onBookingError,
  providerPolicies, // { requireCardOnFile, cancellationWindowHours, noShowFeeType, noShowFeeAmount }
  minimumAdvanceNoticeHours = 0, // Hours before a slot that clients can no longer book it
  providerTimezone, // Provider's timezone from database (required for converting UTC bookings)
  currencySymbol = "$",
  currencyCode = "CAD",
}) => {
  // Chosen date/time
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");

  // Phone modal state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneModalData, setPhoneModalData] = useState({
    countryCode: "+1", // Default to US/Canada
    phone: "",
    fullName: "",
    email: "",
    notes: "",
  });
  const [phoneModalLoading, setPhoneModalLoading] = useState(false);
  const [phoneModalError, setPhoneModalError] = useState(null);
  const [isNewClient, setIsNewClient] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);

  // Loading state for booking
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Handle Add-On selection
  const handleAddonToggle = (addonId) => {
    setSelectedAddons((prev) => {
      if (prev.includes(addonId)) {
        return prev.filter((id) => id !== addonId);
      } else {
        return [...prev, addonId];
      }
    });
  };

  // Handle phone number check/submit
  const handlePhoneSubmit = async () => {
    setPhoneModalError(null);

    if (!phoneModalData.phone.trim()) {
      setPhoneModalError("Please enter your phone number.");
      return;
    }

    setPhoneModalLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/find-or-create-public`,
        {
          providerId,
          countryCode: phoneModalData.countryCode || "+1",
          phone: phoneModalData.phone.trim(),
          fullName: phoneModalData.fullName.trim() || null,
          email: phoneModalData.email.trim() || null,
        }
      );

      const { client } = response.data;
      setClientInfo(client);

      // If we have client info, proceed with booking
      if (client) {
        await handleBooking(client);
      }
    } catch (err) {
      console.error("Error finding/creating client:", err.response?.data || err.message);

      if (err.response?.status === 400 && err.response.data?.requiresFullName) {
        // New client - need full name
        setIsNewClient(true);
        setPhoneModalLoading(false); // Reset loading so user can enter info
        setPhoneModalError("Please enter your full name and email to continue.");
      } else if (err.response?.status === 403) {
        setPhoneModalError(err.response.data?.message || "You are unable to book with this provider.");
        setPhoneModalLoading(false);
      } else {
        setPhoneModalError(err.response?.data?.message || "Failed to process your information. Please try again.");
        setPhoneModalLoading(false);
      }
    }
  };

  const navigate = useNavigate();

  // Submit booking
  const handleBooking = async (client) => {
    // Clear any previous errors
    if (onBookingError) {
      onBookingError(null);
    }

    // Validate required fields
    if (!selectedDate || !selectedTime) {
      if (onBookingError) {
        onBookingError("Please select a date and time for your booking.");
      } else {
        alert("Please select a date and time.");
      }
      setPhoneModalLoading(false);
      return;
    }

    if (!client) {
      if (onBookingError) {
        onBookingError("Client information is required.");
      } else {
        alert("Client information is required.");
      }
      setPhoneModalLoading(false);
      return;
    }

    setBookingLoading(true);

    try {
      // Build dateTime object
      const dateStr = format(selectedDate, "yyyy-MM-dd");

      const sanitizeTime = (timeValue) => {
        if (!timeValue) return "00:00";
        if (timeValue.length >= 5) return timeValue.slice(0, 5);
        return timeValue.padStart(5, "0");
      };

      const toMinutes = (timeValue) => {
        const [h = 0, m = 0] = sanitizeTime(timeValue).split(":").map(Number);
        return h * 60 + m;
      };

      const minutesToTime = (minutesTotal) => {
        const normalized = ((minutesTotal % (24 * 60)) + 24 * 60) % (24 * 60);
        const hours = String(Math.floor(normalized / 60)).padStart(2, "0");
        const minutes = String(normalized % 60).padStart(2, "0");
        return `${hours}:${minutes}`;
      };

      const startTimePlain = sanitizeTime(selectedTime);
      // Service model uses durationMinutes, not duration
      const durationMinutes = Number(selectedServiceDetail.durationMinutes || selectedServiceDetail.duration || 60);
      const endTimePlain = minutesToTime(
        toMinutes(startTimePlain) + durationMinutes
      );

      const startTimeStr = `${startTimePlain}:00`;
      const endTimeStr = `${endTimePlain}:00`;

      // Build payload matching the new backend structure (public booking)
      const startAt = `${dateStr}T${startTimeStr}`;
      const endAt = `${dateStr}T${endTimeStr}`;

      const payload = {
        providerId,
        clientId: client.id,
        serviceId: selectedServiceDetail.id,
        addonIds: selectedAddons,
        startAt,
        endAt,
        timeZone: selectedTimezone || "America/New_York",
        notesFromClient: phoneModalData.notes.trim() || null,
        channel: 'manual',
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/public`,
        payload
      );

      // Close modal
      setShowPhoneModal(false);

      // Handle navigation based on response
      if (res.data.paymentLink) {
        // Redirect to Stripe payment link
        window.location.href = res.data.paymentLink;
      } else {
        // Success - show confirmation modal
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error("Booking error:", err.response?.data || err.message);

      // Handle different types of errors with user-friendly messages
      let errorMessage = "Booking failed. Please try again.";

      if (err.response?.status === 400) {
        errorMessage = err.response.data?.message || "Invalid booking request. Please check your details and try again.";
      } else if (err.response?.status === 403) {
        errorMessage = err.response.data?.message || "You are unable to book with this provider.";
      } else if (err.response?.status === 404) {
        errorMessage = "Service or provider not found. Please refresh the page and try again.";
      } else if (err.response?.status === 409) {
        errorMessage = "This time slot is already booked. Please select a different time.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        errorMessage = "Network error. Please check your internet connection and try again.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      if (onBookingError) {
        onBookingError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setBookingLoading(false);
      setPhoneModalLoading(false);
    }
  };

  // Handle Book button click - opens phone modal
  const handleBookButtonClick = () => {
    if (!selectedDate || !selectedTime) {
      if (onBookingError) {
        onBookingError("Please select a date and time for your booking.");
      } else {
        alert("Please select a date and time.");
      }
      return;
    }
    setShowPhoneModal(true);
    setIsNewClient(false);
    setPhoneModalError(null);
    setPhoneModalLoading(false); // Ensure loading is false when modal opens
    setBookingLoading(false); // Ensure booking loading is false
    // Reset selected addons when opening modal (user can reselect)
    setSelectedAddons([]);
    setPhoneModalData({
      countryCode: "+1", // Default to US/Canada
      phone: "",
      fullName: "",
      email: "",
      notes: "",
    });
  };

  return (
    <>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all">
            <div className="flex justify-center mb-4">
              <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 text-5xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-6 font-medium">
              Your appointment has been successfully scheduled.
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                window.location.reload();
              }}
              className="w-full px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-lg transition-colors duration-200 shadow-md uppercase tracking-wide text-sm"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Phone Number Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Complete Your Booking</h2>
              <button
                onClick={() => {
                  setShowPhoneModal(false);
                  setPhoneModalError(null);
                  setIsNewClient(false);
                  setPhoneModalLoading(false); // Reset loading when closing
                  setBookingLoading(false); // Reset booking loading when closing
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {phoneModalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{phoneModalError}</p>
              </div>
            )}

            {/* Policy Notice - Card on File Required */}
            {providerPolicies?.requireCardOnFile && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <span className="text-amber-600 mr-2">⚠️</span>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Card Required for This Booking</p>
                    <p className="text-amber-700">
                      This provider requires a card on file.
                      {providerPolicies.cancellationWindowHours > 0 && (
                        <> You may cancel for free up to <strong>{providerPolicies.cancellationWindowHours} hour{providerPolicies.cancellationWindowHours !== 1 ? 's' : ''}</strong> before your appointment.</>
                      )}
                    </p>
                    {providerPolicies.noShowFeeAmount > 0 && (
                      <p className="mt-1 text-amber-700">
                        {providerPolicies.noShowFeeType === 'percentage' ? (
                          <>No-shows or late cancellations will be charged <strong>{parseFloat(providerPolicies.noShowFeeAmount).toFixed(0)}%</strong> of the booking total.</>
                        ) : (
                          <>No-shows or late cancellations will be charged <strong>{currencySymbol}{parseFloat(providerPolicies.noShowFeeAmount).toFixed(2)} {currencyCode}</strong>.</>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-gray-700 font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {/* Country Code Selector */}
                  <select
                    value={phoneModalData.countryCode || "+1"}
                    onChange={(e) =>
                      setPhoneModalData((prev) => ({ ...prev, countryCode: e.target.value }))
                    }
                    className="rounded-lg p-2 sm:p-3 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-[100px] sm:w-[120px] text-sm sm:text-base"
                    disabled={bookingLoading}
                  >
                    {countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    value={phoneModalData.phone}
                    onChange={(e) =>
                      setPhoneModalData((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="flex-1 min-w-0 rounded-lg p-2 sm:p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                    placeholder="(555) 123-4567"
                    disabled={bookingLoading}
                    autoFocus
                  />
                </div>
              </div>

              {isNewClient && (
                <>
                  <div>
                    <label className="block mb-2 text-gray-700 font-medium">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={phoneModalData.fullName}
                      onChange={(e) =>
                        setPhoneModalData((prev) => ({ ...prev, fullName: e.target.value }))
                      }
                      className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="John Doe"
                      disabled={bookingLoading}
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-gray-700 font-medium">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={phoneModalData.email}
                      onChange={(e) =>
                        setPhoneModalData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="john@example.com"
                      disabled={bookingLoading}
                    />
                  </div>
                </>
              )}

              {/* Add-Ons Selection */}
              {(selectedServiceDetail.Addons || selectedServiceDetail.addons) &&
                (selectedServiceDetail.Addons || selectedServiceDetail.addons).length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block mb-3 text-gray-700 font-medium">
                      Add-Ons (Optional)
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedServiceDetail.Addons || selectedServiceDetail.addons).map((addon) => (
                        <div key={addon.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`modal-addon-${addon.id}`}
                            checked={selectedAddons.includes(addon.id)}
                            onChange={() => handleAddonToggle(addon.id)}
                            className="mr-3 accent-purple-600"
                            disabled={bookingLoading}
                          />
                          <label
                            htmlFor={`modal-addon-${addon.id}`}
                            className="text-gray-700 cursor-pointer flex-1"
                          >
                            {addon.name} — <span className="text-purple-600 font-semibold">{currencySymbol}{addon.price} {currencyCode}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <div>
                <label className="block mb-2 text-gray-700 font-medium">Notes (Optional)</label>
                <textarea
                  value={phoneModalData.notes}
                  onChange={(e) =>
                    setPhoneModalData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={3}
                  placeholder="Any special requests or notes for your booking..."
                  disabled={bookingLoading}
                />
              </div>

              <button
                onClick={handlePhoneSubmit}
                disabled={bookingLoading}
                className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {bookingLoading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Booking...
                  </>
                ) : phoneModalLoading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Processing...
                  </>
                ) : (
                  "Complete Booking"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Booking Flow */}
      <div className="space-y-4">
        {/* ====== SERVICE DETAILS ====== */}
        <div className="bg-white border border-gray-200 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
          <div className="flex flex-1 justify-between items-center">
            <button
              className="flex items-center flex-row text-gray-700 hover:text-gray-900 transition-colors"
              onClick={() => setShowBookingFlow(false)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <h1 className="ml-2">Back</h1>
            </button>
            <div className="text-center">
              <h1 className="text-gray-900 font-semibold text-xl">{selectedServiceDetail.name}</h1>
              <h1 className="text-purple-600 font-bold text-lg">{currencySymbol}{selectedServiceDetail.basePrice || selectedServiceDetail.price} {currencyCode}</h1>
            </div>
            {/* Right column (Empty placeholder) */}
            <div className="" />
          </div>
          {selectedServiceDetail.DJserviceMP3 && selectedServiceDetail.DJserviceMP3.length > 0 && (
            <div className="mt-2 flex flex-row justify-center items-center">
              <AudioPlayer
                audioUrl={selectedServiceDetail.DJserviceMP3}
              />
            </div>
          )}
        </div>
        {/* ====== DATE & TIME SELECTION ====== */}
        <DateSelection
          availability={availability}
          bookings={bookings}
          blockedTimes={blockedTimes}
          serviceDuration={Number(selectedServiceDetail.durationMinutes || selectedServiceDetail.duration || 60)}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          selectedTimezone={selectedTimezone}
          onTimezoneChange={onTimezoneChange}
          providerTimezone={providerTimezone}
          minimumAdvanceNoticeHours={minimumAdvanceNoticeHours}
        />

        {/* ====== BOOK BUTTON ====== */}
        <button
          onClick={handleBookButtonClick}
          className="mt-4 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg flex items-center justify-center transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!selectedDate || !selectedTime}
        >
          Book
        </button>
      </div>
    </>
  );
};

export default BookingFlow;
